"use client";
import Modal, { Variant } from "@/components/Modal";
import { BuilderBlockProps } from "@/types";
import { BuilderContextInterface } from "@builder.io/sdk-react";
import {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";

type ModalBlockProps = {
  isOpen?: boolean;
  mount?: boolean;
  modalId: string;
  variant: Variant;
  idleTime?: number;
  closeAnalytics?: { dld: string; dlt: string };
  builderContext: BuilderContextInterface;
  children: ReactNode;
  onClose: () => void;
} & BuilderBlockProps;
export const ModalBlock: FC<PropsWithChildren<ModalBlockProps>> = ({
  isOpen,
  mount = false,
  modalId,
  variant,
  idleTime = 60,
  closeAnalytics,
  builderContext,
  children,
  onClose,
  attributes,
}: ModalBlockProps) => {
  const leaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasBeenDismissedRef = useRef<boolean>(false);
  const isExitIntent = variant === "exitIntent";

  // Take value from state if exists, otherwise initial value or fallback to default (false)
  const open =
    builderContext.rootState?.[modalId] !== undefined
      ? Boolean(builderContext.rootState?.[modalId])
      : (isOpen ?? false);

  const handleCursorLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      if (builderContext?.rootSetState) {
        builderContext.rootSetState({
          ...builderContext.rootState,
          [modalId]: true,
        });
      }
    }, 2000);
  }, [builderContext, modalId]);

  const handleVisitorIdle = useCallback(() => {
    const handleIdleTimeout = () => {
      if (builderContext?.rootSetState) {
        builderContext.rootSetState({
          ...builderContext.rootState,
          [modalId]: true,
        });
      }
    };
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }

    idleTimerRef.current = setTimeout(handleIdleTimeout, idleTime * 1000);
  }, [idleTime, builderContext, modalId]);

  const removeListeners = useCallback(() => {
    document.removeEventListener("mouseleave", handleCursorLeave);
    document.removeEventListener("mousemove", handleVisitorIdle);

    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
    }
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
  }, [handleCursorLeave, handleVisitorIdle]);

  const handleClose = () => {
    if (builderContext?.rootSetState) {
      builderContext.rootSetState({
        ...builderContext.rootState,
        [modalId]: false,
      });
      onClose?.();
      if (isExitIntent) {
        hasBeenDismissedRef.current = true;
        removeListeners();
      }
    }
  };

  useEffect(() => {
    if (isExitIntent && !hasBeenDismissedRef.current) {
      document.addEventListener("mouseleave", handleCursorLeave);
      document.addEventListener("mousemove", handleVisitorIdle);
      return removeListeners;
    }
  }, [
    idleTime,
    isExitIntent,
    handleVisitorIdle,
    removeListeners,
    handleCursorLeave,
  ]);

  return (
    <Modal
      as="div"
      open={open}
      unmount={!mount}
      onClose={handleClose}
      variant={variant}
      closeAnalytics={closeAnalytics}
      {...attributes}
    >
      <div className="flex h-auto flex-col">{children}</div>
    </Modal>
  );
};
